import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ClaimItem } from '@1po/1po-bff-fe-spec/generated/claim/request/CreateOrderClaimRequest';
import { getOrderClaimForm, setOrderClaimForm } from 'domains/order/Order.store';
import { getTextMandatory, isHidden } from 'pages/MyOrdersPage/OrderClaims/claimForm.utils';
import {
  makeKey,
  MultiItemsRulesData,
  MULTI_ITEMS_RULES_DATA,
} from 'pages/MyOrdersPage/OrderClaims/claimFormRulesData';
import { FlexRow } from 'pages/MyOrdersPage/OrderClaims/OrderClaims.styled';
import { Flex, Input, InputNumber, Text } from 'UI';

const MAX_VISIBLE_ITEMS = 14;

const FieldWrapper = ({
  visible,
  children,
  maxWidth = 200,
}: {
  visible: boolean;
  children: React.ReactNode;
  maxWidth?: number;
}) => {
  if (!visible) {
    return null;
  }

  return (
    <Flex direction={'column'} maxWidth={maxWidth}>
      {children}
    </Flex>
  );
};

const areAllFieldsHidden = (fields: Array<MultiItemsRulesData[keyof MultiItemsRulesData] | undefined>) => {
  for (const field of fields) {
    if (!isHidden(field)) {
      return false;
    }
  }
  return true;
};

const MultiItemsSection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const claimForm = useSelector(getOrderClaimForm);

  const updateQuantityClaimed = useCallback(
    (index: number, quantityClaimed: number | undefined) => {
      if (quantityClaimed === undefined || quantityClaimed === null) {
        return;
      }
      const formClaimItems = (claimForm.claimItems ?? []).map((claimItem, claimItemIndex) =>
        claimItemIndex === index ? { ...claimItem, quantityClaimed } : claimItem,
      );
      dispatch(setOrderClaimForm({ ...claimForm, claimItems: formClaimItems }));
    },
    [dispatch, claimForm],
  );

  const rulesData =
    MULTI_ITEMS_RULES_DATA[
      makeKey({
        formType: claimForm.formType,
        reason: claimForm.reason,
        option: claimForm.option,
      })
    ];

  const isReferenceNumberVisible = !isHidden(rulesData?.referenceNumber);
  const isQuantityInvoicedVisible = !isHidden(rulesData?.quantityInvoiced);
  const isQuantityClaimedVisible = !isHidden(rulesData?.quantityClaimed);

  const isMultiItemsSectionHidden = areAllFieldsHidden([
    rulesData?.referenceNumber,
    rulesData?.quantityInvoiced,
    rulesData?.quantityClaimed,
  ]);

  if (isMultiItemsSectionHidden) {
    return null;
  }

  const header = (
    <>
      <FieldWrapper visible={isReferenceNumberVisible}>
        <Text type="base" noWrap>
          {getTextMandatory(
            t('order.reference_table.reference_number', 'Reference number'),
            rulesData?.referenceNumber,
          )}
        </Text>
      </FieldWrapper>

      <FieldWrapper visible={isQuantityInvoicedVisible}>
        <Text type="base" noWrap>
          {getTextMandatory(
            t('order.reference_table.invoiced_quantity', 'Invoiced Quantity'),
            rulesData?.quantityInvoiced,
          )}
        </Text>
      </FieldWrapper>

      <FieldWrapper visible={isQuantityClaimedVisible}>
        <Text type="base" noWrap>
          {getTextMandatory(
            t('order.reference_table.claimed_quantity', 'Claimed Quantity'),
            rulesData?.quantityClaimed,
          )}
        </Text>
      </FieldWrapper>
    </>
  );

  const claimItems = claimForm.claimItems ?? [];
  const visibleItems = claimItems.slice(0, MAX_VISIBLE_ITEMS);
  const hiddenCount = Math.max(claimItems.length - MAX_VISIBLE_ITEMS, 0);
  const halfLength = Math.ceil(visibleItems.length / 2);
  const leftColumnItems = visibleItems.slice(0, halfLength);
  const rightColumnItems = visibleItems.slice(halfLength);

  const renderReferenceFields = (claimItem: ClaimItem, index: number) => (
    <>
      <FieldWrapper visible={isReferenceNumberVisible}>
        <Input bordered disabled value={claimItem.referenceNumber} />
      </FieldWrapper>

      <FieldWrapper visible={isQuantityInvoicedVisible}>
        <InputNumber bordered disabled value={claimItem.quantityInvoiced} />
      </FieldWrapper>

      <FieldWrapper visible={isQuantityClaimedVisible}>
        <InputNumber
          bordered
          min={0}
          max={99999}
          value={claimItem.quantityClaimed}
          onChangeDebounced={(quantityClaimed) => updateQuantityClaimed(index, quantityClaimed)}
        />
      </FieldWrapper>
    </>
  );

  const renderColumn = (columnClaimItems: ClaimItem[], indexOffset: number) => {
    if (columnClaimItems.length === 0) {
      return null;
    }

    return (
      <Flex direction={'column'} gap={10} shrink={0}>
        <FlexRow gap={30} shrink={0}>
          {header}
        </FlexRow>

        {columnClaimItems.map((reference, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <FlexRow gap={30} shrink={0} key={indexOffset + rowIndex}>
            {renderReferenceFields(reference, indexOffset + rowIndex)}
          </FlexRow>
        ))}
      </Flex>
    );
  };

  return (
    <Flex direction={'column'} gap={10} overflowX={'auto'}>
      <FlexRow gap={60} shrink={0} align={'flex-start'}>
        {renderColumn(leftColumnItems, 0)}
        {renderColumn(rightColumnItems, halfLength)}
      </FlexRow>
      {hiddenCount > 0 && (
        <Text type="base" noWrap>
          {`+ ${hiddenCount} ${t('order.claim.exadis_return.more_references', 'more references')}`}
        </Text>
      )}
    </Flex>
  );
};

export default MultiItemsSection;
